import socketIo from 'socket.io-client';

// Get socket URL with fallback
const getSocketUrl = () => {
    let url = process.env.VUE_APP_SOCKET_URL || 'https://api.novacasino.bet';
    
    // Ensure URL has protocol
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
    }
    
    // Remove trailing slash as socket.io adds paths automatically
    url = url.replace(/\/+$/, '');
    
    // Log URL in all modes for debugging
    console.log('Socket URL:', url);
    console.log('VUE_APP_SOCKET_URL env:', process.env.VUE_APP_SOCKET_URL);
    
    return url;
};

// Track error logging to prevent spam
let lastErrorLogTime = {};
const ERROR_LOG_THROTTLE = 10000; // Log errors at most once every 10 seconds per socket

const shouldLogError = (socketName) => {
    const now = Date.now();
    if (!lastErrorLogTime[socketName] || (now - lastErrorLogTime[socketName]) > ERROR_LOG_THROTTLE) {
        lastErrorLogTime[socketName] = now;
        return true;
    }
    return false;
};

const isValidAuthToken = (token) => {
    if (token === undefined || token === null) {
        return false;
    }

    const normalizedToken = String(token).trim();
    return normalizedToken.length > 0 && normalizedToken !== 'null' && normalizedToken !== 'undefined';
};

const setSocketAuthToken = (socketInstance, token) => {
    if (isValidAuthToken(token)) {
        socketInstance.auth.token = String(token).trim();
        return;
    }

    // Guest mode: omit token field so backend does not attempt JWT verify.
    delete socketInstance.auth.token;
};

// Socket configuration with fallback transport
// Try polling first as it's more reliable, then upgrade to websocket if available
const socketConfig = {
    auth: {},
    autoConnect: false,
    reconnection: true,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: 5,
    timeout: 20000,
    transports: ['websocket'], // Match backend – server only accepts websocket
    upgrade: true, // Allow upgrade from polling to websocket
    rememberUpgrade: false, // Don't remember failed websocket attempts
    forceNew: false, // Don't force new connection
    withCredentials: true, // Include credentials for CORS
    extraHeaders: {} // Additional headers if needed
};

const state = {
    socketSendLoading: null,
    socketGeneral: socketIo(getSocketUrl()+'/general', socketConfig),
    socketCrash: socketIo(getSocketUrl()+'/crash', socketConfig),
    socketRoll: socketIo(getSocketUrl()+'/roll', socketConfig),
    socketBlackjack: socketIo(getSocketUrl()+'/blackjack', socketConfig),
    socketDuels: socketIo(getSocketUrl()+'/duels', socketConfig),
    socketMines: socketIo(getSocketUrl()+'/mines', socketConfig),
    socketTowers: socketIo(getSocketUrl()+'/towers', socketConfig),
    socketUnbox: socketIo(getSocketUrl()+'/unbox', socketConfig),
    socketBattles: socketIo(getSocketUrl()+'/battles', socketConfig),
    socketUpgrader: socketIo(getSocketUrl()+'/upgrader', socketConfig),
    socketCashier: socketIo(getSocketUrl()+'/cashier', socketConfig),
    socketAdmin: socketIo(getSocketUrl()+'/admin', socketConfig)
}

const getters = {
    socketSendLoading: state => state.socketSendLoading,
    socketGeneral: state => state.socketGeneral,
    socketCrash: state => state.socketCrash,
    socketRoll: state => state.socketRoll,
    socketBlackjack: state => state.socketBlackjack,
    socketDuels: state => state.socketDuels,
    socketMines: state => state.socketMines,
    socketTowers: state => state.socketTowers,
    socketUnbox: state => state.socketUnbox,
    socketBattles: state => state.socketBattles,
    socketUpgrader: state => state.socketUpgrader,
    socketCashier: state => state.socketCashier,
    socketAdmin: state => state.socketAdmin
}

const mutations = {
    socket_set_send_loading(state, value) {
       state.socketSendLoading = value;
   }
}

const actions = {
    socketSetSendLoading({ commit }, value) {
        commit('socket_set_send_loading', value);
    },
    socketConnectGeneral({ getters, dispatch, rootGetters }) {
        // Set default settings immediately as fallback to prevent infinite loading
        if(rootGetters.generalSettings === null) {
            dispatch('generalSocketConnectionError', null, { root: true });
        }

        // Only connect if not already connected or connecting
        if(getters.socketGeneral.connected === true) {
            getters.socketGeneral.removeAllListeners();
        }
        
        // Set/clear auth token explicitly for guest mode compatibility
        setSocketAuthToken(getters.socketGeneral, rootGetters.authToken);

        // Disconnect first if already connected to reset auth
        if(getters.socketGeneral.connected) {
            getters.socketGeneral.disconnect();
        }

        // Only attempt connection if not already connected
        if(!getters.socketGeneral.connected) {
            try {
                getters.socketGeneral.connect();
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('Socket connection error:', error);
                }
            }
        }

        dispatch('socketListenGeneral');
        
        // Set a timeout to use default settings if connection fails (reduced timeout)
        setTimeout(() => {
            if(rootGetters.generalSettings === null || (rootGetters.generalSettings.general && rootGetters.generalSettings.general.maintenance === undefined)) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Socket connection timeout - using default settings');
                }
                dispatch('generalSocketConnectionError', null, { root: true });
            }
        }, 5000); // 5 second timeout (reduced from 10)
    },
    socketDisconnectGeneral({ getters }) {
        getters.socketGeneral.removeAllListeners();
        getters.socketGeneral.disconnect();
    },
    socketListenGeneral({ getters, dispatch, rootGetters }) {
        getters.socketGeneral.on('init', (data) => { dispatch('generalSocketInit', data); dispatch('chatSocketInit', data); });
        getters.socketGeneral.on('settings', (data) => { dispatch('generalSocketSettings', data); });
        getters.socketGeneral.on('user', (data) => { dispatch('generalSocketUser', data); });
        getters.socketGeneral.on('bet', (data) => { dispatch('generalSocketBet', data); });
        getters.socketGeneral.on('rain', (data) => { dispatch('generalSocketRain', data); });
        getters.socketGeneral.on('userTip', (data) => { dispatch('userTipSocket', data); });
        getters.socketGeneral.on('chatOnline', (data) => { dispatch('chatSocketOnline', data); });
        getters.socketGeneral.on('chatMessage', (data) => { dispatch('chatSocketMessage', data); });
        getters.socketGeneral.on('chatRemove', (data) => { dispatch('chatSocketRemove', data); });
        getters.socketGeneral.on('chatClear', (data) => { dispatch('chatSocketClear', data); });
        getters.socketGeneral.on('rainPayout', (data) => { dispatch('rainPayoutSocket', data); });
        getters.socketGeneral.on('supportTicket', (data) => { dispatch('supportSocketTicket', data); });
        getters.socketGeneral.on('supportTicketCreated', (data) => { dispatch('supportSocketTicketCreated', data); });
        getters.socketGeneral.on('supportTicketUpdated', (data) => { dispatch('supportSocketTicket', data); });
        getters.socketGeneral.on('supportTicketMessage', (data) => { dispatch('supportSocketTicketMessage', data); });
        getters.socketGeneral.on('connect_error', (error) => {
            // Throttle error logging to prevent console spam
            if (shouldLogError('general')) {
                const hasToken = rootGetters && rootGetters.authToken !== null;
                console.warn('Socket connection error (general):', error.message || error);
                console.log('Socket URL:', getSocketUrl() + '/general');
                console.log('Auth token present:', hasToken);
                if (error) {
                    console.log('Error type:', error.type);
                    console.log('Error description:', error.description);
                }
            }
            // Don't set default settings on every error, only if settings are still null after timeout
        });
        getters.socketGeneral.on('connect', () => {
            if (process.env.NODE_ENV === 'development') {
                console.log('Socket connected successfully to:', getSocketUrl() + '/general');
            }
            // Reset error log time on successful connection
            lastErrorLogTime['general'] = 0;
        });
        getters.socketGeneral.on('disconnect', (reason) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('Socket disconnected (general):', reason);
            }
        });
        getters.socketGeneral.on('connect_timeout', () => {
            if (shouldLogError('general') && process.env.NODE_ENV === 'development') {
                console.warn('Socket connection timeout (general)');
            }
        });
        getters.socketGeneral.on('reconnect_attempt', (attemptNumber) => {
            if (shouldLogError('general') && process.env.NODE_ENV === 'development') {
                console.log(`Socket reconnection attempt ${attemptNumber} (general)`);
            }
        });
        getters.socketGeneral.on('reconnect_failed', () => {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Socket reconnection failed (general). Please check your connection.');
            }
        });
    },
    socketConnectCrash({ getters, dispatch, rootGetters }) {
        if(getters.socketCrash.connected === true) { getters.socketCrash.removeAllListeners(); }

        setSocketAuthToken(getters.socketCrash, rootGetters.authToken);

        getters.socketCrash.disconnect();
        getters.socketCrash.connect();

        dispatch('socketListenCrash');
    },
    socketDisconnectCrash({ getters }) {
        getters.socketCrash.removeAllListeners();
        getters.socketCrash.disconnect();
    },
    socketListenCrash({ getters, dispatch }) {
        getters.socketCrash.on('init', (data) => { dispatch('crashSocketInit', data); });
        getters.socketCrash.on('game', (data) => { dispatch('crashSocketGame', data); });
        getters.socketCrash.on('tick', (data) => { dispatch('crashSocketTick', data); });
        getters.socketCrash.on('bet', (data) => { dispatch('crashSocketBet', data); });
    },
    socketConnectRoll({ getters, dispatch, rootGetters }) {
        if(getters.socketRoll.connected === true) { getters.socketRoll.removeAllListeners(); }

        setSocketAuthToken(getters.socketRoll, rootGetters.authToken);

        getters.socketRoll.disconnect();
        getters.socketRoll.connect();

        dispatch('socketListenRoll');
    },
    socketDisconnectRoll({ getters }) {
        getters.socketRoll.removeAllListeners();
        getters.socketRoll.disconnect();
    },
    socketListenRoll({ getters, dispatch }) {
        getters.socketRoll.on('init', (data) => { dispatch('rollSocketInit', data); });
        getters.socketRoll.on('game', (data) => { dispatch('rollSocketGame', data); });
        getters.socketRoll.on('bet', (data) => { dispatch('rollSocketBet', data); });
    },
    socketConnectBlackjack({ getters, dispatch, rootGetters }) {
        // Only connect if not already connected
        if(getters.socketBlackjack.connected === true) {
            getters.socketBlackjack.removeAllListeners();
        }
        
        setSocketAuthToken(getters.socketBlackjack, rootGetters.authToken);

        // Only attempt connection if not already connected
        if(!getters.socketBlackjack.connected) {
            getters.socketBlackjack.connect();
        }

        dispatch('socketListenBlackjack');
    },
    socketDisconnectBlackjack({ getters }) {
        getters.socketBlackjack.removeAllListeners();
        getters.socketBlackjack.disconnect();
    },
    socketListenBlackjack({ getters, dispatch }) {
        getters.socketBlackjack.on('init', (data) => { dispatch('blackjackSocketInit', data); });
        getters.socketBlackjack.on('table', (data) => { dispatch('blackjackSocketTable', data); });
        getters.socketBlackjack.on('connect_error', (error) => {
            // Throttle error logging to prevent console spam
            if (shouldLogError('blackjack')) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Socket connection error (blackjack):', error.message || error);
                }
            }
        });
        getters.socketBlackjack.on('connect', () => {
            if (process.env.NODE_ENV === 'development') {
                console.log('Socket connected successfully (blackjack)');
            }
            // Reset error log time on successful connection
            lastErrorLogTime['blackjack'] = 0;
        });
        getters.socketBlackjack.on('disconnect', (reason) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('Socket disconnected (blackjack):', reason);
            }
        });
    },
    socketConnectDuels({ getters, dispatch, rootGetters }) {
        if(getters.socketDuels.connected === true) { getters.socketDuels.removeAllListeners(); }
        setSocketAuthToken(getters.socketDuels, rootGetters.authToken);

        getters.socketDuels.disconnect();
        getters.socketDuels.connect();

        dispatch('socketListenDuels');
    },
    socketDisconnectDuels({ getters }) {
        getters.socketDuels.removeAllListeners();
        getters.socketDuels.disconnect();
    },
    socketListenDuels({ getters, dispatch }) {
        getters.socketDuels.on('init', (data) => { dispatch('duelsSocketInit', data); });
        getters.socketDuels.on('game', (data) => { dispatch('duelsSocketGame', data); });
    },
    socketConnectMines({ getters, dispatch, rootGetters }) {
        if(getters.socketMines.connected === true) { getters.socketMines.removeAllListeners(); }
        setSocketAuthToken(getters.socketMines, rootGetters.authToken);

        getters.socketMines.disconnect();
        getters.socketMines.connect();

        dispatch('socketListenMines');
    },
    socketDisconnectMines({ getters }) {
        getters.socketMines.removeAllListeners();
        getters.socketMines.disconnect();
    },
    socketListenMines({ getters, dispatch }) {
        getters.socketMines.on('init', (data) => { dispatch('minesSocketInit', data); });
    },
    socketConnectTowers({ getters, dispatch, rootGetters }) {
        if(getters.socketTowers.connected === true) { getters.socketTowers.removeAllListeners(); }
        setSocketAuthToken(getters.socketTowers, rootGetters.authToken);

        getters.socketTowers.disconnect();
        getters.socketTowers.connect();

        dispatch('socketListenTowers');
    },
    socketDisconnectTowers({ getters }) {
        getters.socketTowers.removeAllListeners();
        getters.socketTowers.disconnect();
    },
    socketListenTowers({ getters, dispatch }) {
        getters.socketTowers.on('init', (data) => { dispatch('towersSocketInit', data); });
    },
    socketConnectUnbox({ getters, dispatch, rootGetters }) {
        if(getters.socketUnbox.connected === true) { getters.socketUnbox.removeAllListeners(); }
        setSocketAuthToken(getters.socketUnbox, rootGetters.authToken);

        getters.socketUnbox.disconnect();
        getters.socketUnbox.connect();

        dispatch('socketListenUnbox');
    },
    socketDisconnectUnbox({ getters }) {
        getters.socketUnbox.removeAllListeners();
        getters.socketUnbox.disconnect();
    },
    socketListenUnbox({ getters, dispatch }) {
        getters.socketUnbox.on('init', (data) => { dispatch('unboxSocketInit', data); });
    },
    socketConnectBattles({ getters, dispatch, rootGetters }) {
        if(getters.socketBattles.connected === true) { getters.socketBattles.removeAllListeners(); }
        setSocketAuthToken(getters.socketBattles, rootGetters.authToken);

        getters.socketBattles.disconnect();
        getters.socketBattles.connect();

        dispatch('socketListenBattles');
    },
    socketDisconnectBattles({ getters }) {
        getters.socketBattles.removeAllListeners();
        getters.socketBattles.disconnect();
    },
    socketListenBattles({ getters, dispatch }) {
        getters.socketBattles.on('init', (data) => { dispatch('battlesSocketInit', data); });
        getters.socketBattles.on('game', (data) => { dispatch('battlesSocketGame', data); });
    },
    socketConnectUpgrader({ getters, dispatch, rootGetters }) {
        if(getters.socketUpgrader.connected === true) { getters.socketUpgrader.removeAllListeners(); }
        setSocketAuthToken(getters.socketUpgrader, rootGetters.authToken);

        getters.socketUpgrader.disconnect();
        getters.socketUpgrader.connect();

        dispatch('socketListenUpgrader');
    },
    socketDisconnectUpgrader({ getters }) {
        getters.socketUpgrader.removeAllListeners();
        getters.socketUpgrader.disconnect();
    },
    socketListenUpgrader({ getters, dispatch }) {
        
    },
    socketConnectCashier({ getters, dispatch, rootGetters }) {
        if(getters.socketCashier.connected === true) { getters.socketCashier.removeAllListeners(); }
        setSocketAuthToken(getters.socketCashier, rootGetters.authToken);

        getters.socketCashier.disconnect();
        getters.socketCashier.connect();

        dispatch('socketListenCashier');
    },
    socketDisconnectCashier({ getters }) {
        getters.socketCashier.removeAllListeners();
        getters.socketCashier.disconnect();
    },
    socketListenCashier({ getters, dispatch }) {
        getters.socketCashier.on('robuxOffer', (data) => { dispatch('cashierSocketRobuxOffer', data); });
        getters.socketCashier.on('limitedTransaction', (data) => { dispatch('cashierSocketLimitedTransaction', data); });
        getters.socketCashier.on('cryptoTransaction', (data) => { dispatch('cashierSocketCryptoTransaction', data); });
    },
    socketConnectAdmin({ getters, dispatch, rootGetters }) {
        if(getters.socketAdmin.connected === true) { getters.socketAdmin.removeAllListeners(); }
        setSocketAuthToken(getters.socketAdmin, rootGetters.authToken);

        getters.socketAdmin.disconnect();
        getters.socketAdmin.connect();
        console.log('Socket connected successfully to: admin');
        dispatch('socketListenAdmin');
    },
    socketDisconnectAdmin({ getters }) {
        console.log('Socket disconnected from: admin');
        getters.socketAdmin.removeAllListeners();   
        getters.socketAdmin.disconnect();
    },
    socketListenAdmin({ getters, dispatch }) {
        console.log('Socket listening to: admin');
        getters.socketAdmin.on('settings', (data) => { dispatch('adminSocketSettings', data); });
        getters.socketAdmin.on('supportTicket', (data) => { dispatch('supportSocketTicket', data); });
        getters.socketAdmin.on('supportTicketCreated', (data) => { dispatch('supportSocketTicketCreated', data); });
        getters.socketAdmin.on('supportTicketUpdated', (data) => { dispatch('supportSocketTicket', data); });
        getters.socketAdmin.on('supportTicketMessage', (data) => { dispatch('supportSocketTicketMessage', data); });
    },
    // Helper function to connect game socket based on current route
    socketConnectGameByRoute({ dispatch, rootGetters }, routeName) {
        // Map route names to socket connection actions
        const routeToSocketMap = {
            'Crash': 'socketConnectCrash',
            'Roll': 'socketConnectRoll',
            'Blackjack': 'socketConnectBlackjack',
            'BlackjackOverview': 'socketConnectBlackjack',
            'BlackjackTables': 'socketConnectBlackjack',
            'BlackjackTable': 'socketConnectBlackjack',
            'Duels': 'socketConnectDuels',
            'Mines': 'socketConnectMines',
            'Towers': 'socketConnectTowers',
            'Unbox': 'socketConnectUnbox',
            'UnboxOverview': 'socketConnectUnbox',
            'UnboxBox': 'socketConnectUnbox',
            'Battles': 'socketConnectBattles',
            'BattlesOverview': 'socketConnectBattles',
            'BattlesGame': 'socketConnectBattles',
            'BattlesCreate': 'socketConnectBattles',
            'Upgrader': 'socketConnectUpgrader'
        };

        const socketAction = routeToSocketMap[routeName];
        if(socketAction) {
            dispatch(socketAction);
        }
    }
}

const socket = {
    state,
    mutations,
    actions,
    getters
}

export default socket;
